import React from 'react';
import { Download, RefreshCw, AlertTriangle, X, ArrowUpCircle } from 'lucide-react';
import { APP_VERSION, getAppPlatform } from '../../utils/version';
import logo from '../../utils/marketlite-logo.png';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.marketlite.app';

/**
 * ForceUpdateModal
 *
 * Props from useForceUpdate:
 *   status        — 'force-update' | 'soft-update'
 *   latestVersion — string
 *   updateMessage — string | null
 *   downloadUrl   — string | null
 *   dismiss       — fn (only for soft-update)
 */
const ForceUpdateModal = ({ status, latestVersion, updateMessage, downloadUrl, dismiss }) => {
  if (status !== 'force-update' && status !== 'soft-update') return null;

  const isForce = status === 'force-update';
  const platform = getAppPlatform();
  const storeUrl = downloadUrl || (platform === 'ios'
    ? 'https://apps.apple.com/app/marketlite'
    : PLAY_STORE_URL);

  const handleUpdate = () => {
    if (platform === 'web') {
      // PWA: hard reload to pick up new service worker
      window.location.reload(true);
    } else {
      window.open(storeUrl, '_blank', 'noopener');
    }
  };

  const message = updateMessage ||
    (isForce
      ? 'A required update is available. Please update to continue using MarketLite.'
      : 'A new version of MarketLite is available with improvements and bug fixes.');

  // ── Soft update: slim banner at top ──────────────────────────────────────
  if (!isForce) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9998] flex items-center justify-between gap-3 px-4 py-3 bg-indigo-600 text-white shadow-lg"
           style={{ paddingTop: 'env(safe-area-inset-top, 12px)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <ArrowUpCircle size={18} className="shrink-0 text-indigo-200" />
          <p className="text-sm font-medium truncate">
            v{latestVersion} is available — {message}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleUpdate}
            className="px-3 py-1 bg-white text-indigo-700 font-semibold text-xs rounded-lg hover:bg-indigo-50 active:scale-95 transition-transform"
          >
            Update
          </button>
          <button onClick={dismiss} className="p-1 hover:bg-indigo-700 rounded-lg transition-colors">
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  // ── Force update: full-screen blocking modal ──────────────────────────────
  return (
    <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-teal-900 to-indigo-950 px-6 select-none"
         style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Decorative blobs */}
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 -right-20 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center max-w-sm w-full space-y-7">
        {/* Logo */}
        <div className="w-24 h-24 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-2xl shadow-black/30 overflow-hidden">
          <img src={logo} alt="MarketLite" className="w-20 h-20 object-contain" />
        </div>

        {/* Badge */}
        <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-400/20 border border-amber-400/30 rounded-full">
          <AlertTriangle size={14} className="text-amber-400 shrink-0" />
          <span className="text-amber-300 text-xs font-semibold uppercase tracking-wide">Update Required</span>
        </div>

        {/* Heading */}
        <div className="space-y-3">
          <h1 className="text-3xl font-extrabold text-white tracking-tight">
            New Version Available
          </h1>
          <p className="text-white/65 text-base leading-relaxed">
            {message}
          </p>
        </div>

        {/* Version chips */}
        <div className="flex items-center justify-center gap-3 w-full">
          <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-center">
            <p className="text-white/40 text-xs mb-1">Your version</p>
            <p className="text-white font-mono font-bold text-sm">v{APP_VERSION}</p>
          </div>
          <RefreshCw size={16} className="text-white/30 shrink-0" />
          <div className="flex-1 bg-teal-500/10 border border-teal-500/20 rounded-2xl px-4 py-3 text-center">
            <p className="text-teal-300/60 text-xs mb-1">Latest version</p>
            <p className="text-teal-300 font-mono font-bold text-sm">v{latestVersion}</p>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={handleUpdate}
          className="w-full py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 font-bold text-base rounded-2xl shadow-2xl shadow-amber-500/30 active:scale-95 transition-transform flex items-center justify-center gap-2"
        >
          <Download size={18} />
          {platform === 'web' ? 'Reload to Update' : 'Update on Play Store'}
        </button>

        <p className="text-white/25 text-xs">
          This update is required to continue using MarketLite
        </p>
      </div>
    </div>
  );
};

export default ForceUpdateModal;
